import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, Ban, Users, DollarSign, ClipboardList, XCircle, Download, Star, KeyRound, Ship, Printer, Image as ImageIcon, Upload, GripVertical } from "lucide-react";
import { api, adminApi, apiError, prettyDate } from "@/lib/api";
import { ToursManager } from "@/components/ToursManager";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("mmc_token", data.access_token);
      onLogin();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success("If that email exists, a reset link has been sent. Check your inbox.");
      setForgotMode(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (forgotMode) {
    return (
      <div className="min-h-screen bg-black pt-28 pb-20 px-4 flex items-start justify-center">
        <form onSubmit={sendReset} className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md mt-10">
          <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-1">Forgot Password</h1>
          <p className="text-sm text-slate-500 mb-6">Enter your email and we'll send you a reset link.</p>
          <input
            data-testid="forgot-email-input"
            type="email"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-sky-400"
            placeholder="Email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
          />
          <button data-testid="forgot-submit-button" type="submit" disabled={busy} className="btn-ocean w-full text-white font-bold py-3 rounded-full disabled:opacity-60 mb-3">
            {busy ? "Sending…" : "Send Reset Link"}
          </button>
          <button type="button" data-testid="forgot-back-button" onClick={() => setForgotMode(false)} className="w-full text-sm font-bold text-slate-500 hover:text-slate-700 py-2">
            Back to sign in
          </button>
        </form>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-black pt-28 pb-20 px-4 flex items-start justify-center">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md mt-10">
        <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-1">Admin Login</h1>
        <p className="text-sm text-slate-500 mb-6">Mickey Mouse Charters staff only</p>
        <input
          data-testid="admin-email-input"
          type="email"
          className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-sky-400"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          data-testid="admin-password-input"
          type="password"
          className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-sky-400"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button data-testid="admin-login-button" type="submit" disabled={busy} className="btn-ocean w-full text-white font-bold py-3 rounded-full disabled:opacity-60 mb-3">
          {busy ? "Signing in…" : "Sign In"}
        </button>
        <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotMode(true); setForgotEmail(email); }} className="w-full text-sm font-bold text-sky-600 hover:text-sky-700 py-2">
          Forgot password?
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
      <Icon size={14} className="text-sky-500" /> {label}
    </div>
    <p className="font-display text-2xl font-extrabold text-slate-900">{value}</p>
  </div>
);

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filterTour, setFilterTour] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [tours, setTours] = useState([]);
  const [pwInputs, setPwInputs] = useState({});
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "partner" });
  const [newBlock, setNewBlock] = useState({ tour_id: "party", date: "" });
  const [expanded, setExpanded] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryCaption, setGalleryCaption] = useState("");
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [captionEdits, setCaptionEdits] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const galleryFileRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const ax = adminApi();
      const params = {};
      if (filterTour) params.tour_id = filterTour;
      if (filterDate) params.date = filterDate;
      const [s, b, bl, rv, us, ts, g] = await Promise.all([
        ax.get("/admin/stats"),
        ax.get("/admin/bookings", { params }),
        ax.get("/admin/blocked-dates"),
        ax.get("/admin/reviews"),
        ax.get("/admin/users"),
        ax.get("/admin/tours"),
        ax.get("/gallery"),
      ]);
      setStats(s.data); setBookings(b.data); setBlocked(bl.data); setReviews(rv.data); setUsers(us.data); setTours(ts.data); setGallery(g.data);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("mmc_token");
        setAuthed(false);
      } else toast.error(apiError(e));
    }
  }, [filterTour, filterDate]);

  useEffect(() => {
    if (localStorage.getItem("mmc_token")) loadAll();
    else setAuthed(false);
  }, [loadAll]);

  const addBlock = async () => {
    if (!newBlock.date) return toast.error("Pick a date");
    try {
      await adminApi().post("/admin/blocked-dates", newBlock);
      toast.success("Date blocked");
      setNewBlock({ ...newBlock, date: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unblock = async (id) => {
    try { await adminApi().delete(`/admin/blocked-dates/${id}`); loadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  const exportManifest = async () => {
    try {
      const params = {};
      if (filterTour) params.tour_id = filterTour;
      if (filterDate) params.date = filterDate;
      const { data } = await adminApi().get("/admin/manifest", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `manifest${filterDate ? "-" + filterDate : ""}${filterTour ? "-" + filterTour : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Manifest downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await adminApi().delete(`/admin/reviews/${id}`); loadAll(); toast.success("Review deleted"); } catch (e) { toast.error(apiError(e)); }
  };

  const approveReview = async (id) => {
    try { await adminApi().post(`/admin/reviews/${id}/approve`); loadAll(); toast.success("Review approved — now live on the home page"); } catch (e) { toast.error(apiError(e)); }
  };

  const changePassword = async (userId) => {
    const pw = pwInputs[userId] || "";
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await adminApi().post(`/admin/users/${userId}/password`, { new_password: pw });
      setPwInputs({ ...pwInputs, [userId]: "" });
      toast.success("Password updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? They will no longer be able to sign in.`)) return;
    try {
      await adminApi().delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createUser = async () => {
    if (!newUser.email.trim() || !newUser.name.trim()) return toast.error("Enter a name and email");
    if (newUser.password.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await adminApi().post("/admin/users", { ...newUser, email: newUser.email.trim(), name: newUser.name.trim() });
      toast.success(`User ${newUser.email.trim()} created`);
      setNewUser({ email: "", name: "", password: "", role: "partner" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadGalleryPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", galleryCaption.trim());
      await adminApi().post("/admin/gallery", fd);
      toast.success("Photo added to the gallery");
      setGalleryCaption("");
      loadAll();
    } catch (err) { toast.error(apiError(err)); }
    finally {
      setGalleryUploading(false);
      if (galleryFileRef.current) galleryFileRef.current.value = "";
    }
  };

  const deleteGalleryPhoto = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try { await adminApi().delete(`/admin/gallery/${id}`); toast.success("Photo removed"); loadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  const handleGalleryDrop = async (targetId) => {
    const fromIdx = gallery.findIndex((p) => p.id === dragId);
    const toIdx = gallery.findIndex((p) => p.id === targetId);
    setDragOverId(null);
    setDragId(null);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...gallery];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setGallery(next);
    try {
      await adminApi().post("/admin/gallery/reorder", { ids: next.map((p) => p.id) });
      toast.success("Gallery order saved");
    } catch (e) {
      toast.error(apiError(e));
      loadAll();
    }
  };

  const saveGalleryCaption = async (id) => {
    try {
      await adminApi().put(`/admin/gallery/${id}`, { caption: captionEdits[id] ?? "" });
      toast.success("Caption updated");
      setCaptionEdits((c) => { const n = { ...c }; delete n[id]; return n; });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const printBoardingSheet = () => {
    if (!filterDate) return toast.error("Select a date filter first to print a boarding sheet");
    const list = bookings.filter((b) => b.status === "confirmed");
    if (list.length === 0) return toast.error("No confirmed bookings for this selection");
    const esc = (s) => String(s ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let rows = "";
    let i = 1;
    for (const b of list) {
      const guests = b.passengers.length
        ? b.passengers
        : Array.from({ length: b.passenger_count || 0 }, () => ({ name: "", age: "" }));
      for (const p of guests) {
        rows += `<tr><td>${i++}</td><td>${esc(p.name)}</td><td>${esc(p.age)}</td><td>${esc(b.code)}</td><td>${esc(b.customer_name)}</td><td></td><td class="sig"></td></tr>`;
      }
    }
    const tourName = tours.find((t) => t.id === filterTour)?.name || "All Tours";
    const times = [...new Set(list.map((b) => b.time).filter(Boolean))].join(" / ");
    const html = `<!doctype html><html><head><title>Boarding Sheet — ${esc(tourName)} ${esc(filterDate)}</title>
<style>
body{font-family:Arial,sans-serif;color:#111;margin:32px}
h1{font-size:20px;margin:0}
p.meta{font-size:13px;color:#444;margin:6px 0 20px}
table{width:100%;border-collapse:collapse;font-size:13px}
th,td{border:1px solid #999;padding:8px 10px;text-align:left}
th{background:#eee;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
td.sig{min-width:180px;height:30px}
.note{font-size:11px;color:#555;margin-top:16px}
@media print{.noprint{display:none}}
</style></head><body>
<h1>Mickey Mouse Charters — Boarding Sheet</h1>
<p class="meta"><strong>${esc(tourName)}</strong> · Date: ${esc(filterDate)}${times ? ` · Time: ${esc(times)}` : ""} · Guests: ${i - 1}</p>
<table><thead><tr><th>#</th><th>Guest Name</th><th>Age</th><th>Booking</th><th>Booked By</th><th>Checked In</th><th>Waiver Signature</th></tr></thead>
<tbody>${rows}</tbody></table>
<p class="note">Every participant must sign the liability waiver before departure. Checked-in guests should initial the box.</p>
<script>window.print()</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to print the boarding sheet");
    w.document.write(html);
    w.document.close();
  };

  if (authed === null) return <div className="min-h-screen pt-32 text-center text-slate-500">Loading…</div>;
  if (!authed) return <Login onLogin={loadAll} />;

  return (
    <div className="min-h-screen bg-slate-100 pt-24 pb-20 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Admin Dashboard</h1>
          <button
            data-testid="admin-logout-button"
            onClick={() => { localStorage.removeItem("mmc_token"); setAuthed(false); }}
            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors duration-150"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={ClipboardList} label="Active bookings" value={stats.bookings} testid="stat-bookings" />
            <StatCard icon={Users} label="Total guests" value={stats.guests} testid="stat-guests" />
            <StatCard icon={DollarSign} label="Expected revenue" value={`$${stats.revenue.toFixed(0)}`} testid="stat-revenue" />
            <StatCard icon={XCircle} label="Cancelled" value={stats.cancelled} testid="stat-cancelled" />
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            ["bookings", "Bookings", ClipboardList],
            ["tours", "Tours", Ship],
            ["gallery", "Gallery", ImageIcon],
            ["blocked", "Blocked Dates", Ban],
            ["reviews", "Reviews", Star],
            ["users", "Users", KeyRound],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-colors duration-150 ${
                tab === id ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center">
              <select
                data-testid="admin-filter-tour"
                value={filterTour}
                onChange={(e) => setFilterTour(e.target.value)}
                className="border border-slate-300 rounded-xl px-3 py-2 text-sm"
              >
                <option value="">All tours</option>
                {tours.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <input
                data-testid="admin-filter-date"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="border border-slate-300 rounded-xl px-3 py-2 text-sm"
              />
              {filterDate && (
                <button onClick={() => setFilterDate("")} className="text-xs font-bold text-slate-400 hover:text-slate-600">Clear date</button>
              )}
              <button
                data-testid="admin-print-boarding-sheet"
                onClick={printBoardingSheet}
                className="ml-auto flex items-center gap-2 border border-slate-300 text-slate-700 text-sm font-bold px-4 py-2 rounded-xl hover:bg-slate-50 transition-colors duration-150"
              >
                <Printer size={15} /> Boarding Sheet
              </button>
              <button
                data-testid="admin-export-manifest-button"
                onClick={exportManifest}
                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors duration-150"
              >
                <Download size={15} /> Export Manifest CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table data-testid="admin-manifest-table" className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Code</th>
                    <th className="text-left px-4 py-3">Tour</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Guests</th>
                    <th className="text-left px-4 py-3">Total</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No bookings yet</td></tr>
                  )}
                  {bookings.map((b) => (
                    <Fragment key={b.code}>
                      <tr
                        onClick={() => setExpanded(expanded === b.code ? null : b.code)}
                        className="border-t border-slate-100 hover:bg-sky-50 cursor-pointer"
                        data-testid={`admin-booking-row-${b.code}`}
                      >
                        <td className="px-4 py-3 font-mono-code font-bold text-sky-600">{b.code}</td>
                        <td className="px-4 py-3">{b.tour_name}</td>
                        <td className="px-4 py-3">{b.date}</td>
                        <td className="px-4 py-3">{b.customer_name}<br /><span className="text-xs text-slate-400">{b.email}</span></td>
                        <td className="px-4 py-3 font-bold">{b.passenger_count ?? b.passengers.length}</td>
                        <td className="px-4 py-3">${b.total.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${b.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                            {b.status}
                          </span>
                        </td>
                      </tr>
                      {expanded === b.code && (
                        <tr className="bg-slate-50">
                          <td colSpan="7" className="px-6 py-4">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Guest manifest</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                              {b.passengers.length === 0 && (
                                <p className="text-sm text-slate-500 italic">{b.passenger_count ?? 0} passengers — names not provided yet</p>
                              )}
                              {b.passengers.map((p, i) => (
                                <p key={i} className="text-sm text-slate-700">{i + 1}. {p.name} — age {p.age}</p>
                              ))}
                            </div>
                            {b.phone && <p className="text-xs text-slate-500 mt-2">Phone: {b.phone}</p>}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "tours" && <ToursManager tours={tours} reload={loadAll} />}

        {tab === "gallery" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Photo gallery</h2>
            <p className="text-sm text-slate-500 mb-5">Upload trip photos for the public Gallery page. Captions are optional. JPG, PNG, WEBP or GIF up to 8 MB. Drag photos to reorder — the first photo shows at the top of the gallery.</p>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center">
              <input
                data-testid="admin-gallery-caption-input"
                placeholder="Caption (optional)"
                value={galleryCaption}
                onChange={(e) => setGalleryCaption(e.target.value)}
                className="border border-slate-300 rounded-xl px-3 py-2 text-sm w-72"
              />
              <button
                data-testid="admin-gallery-upload-button"
                onClick={() => galleryFileRef.current?.click()}
                disabled={galleryUploading}
                className="btn-ocean text-white text-sm font-bold px-5 py-2 rounded-xl flex items-center gap-2 disabled:opacity-60"
              >
                <Upload size={15} /> {galleryUploading ? "Uploading…" : "Upload Photo"}
              </button>
              <input ref={galleryFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadGalleryPhoto} data-testid="admin-gallery-file-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-gallery-grid">
              {gallery.map((p) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => setDragId(p.id)}
                  onDragOver={(e) => { e.preventDefault(); if (p.id !== dragId) setDragOverId(p.id); }}
                  onDragLeave={() => setDragOverId((d) => (d === p.id ? null : d))}
                  onDrop={() => handleGalleryDrop(p.id)}
                  onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                  className={`bg-slate-50 rounded-xl overflow-hidden border cursor-grab active:cursor-grabbing transition-all duration-150 ${
                    dragOverId === p.id ? "border-sky-500 ring-2 ring-sky-300 scale-[1.02]" : "border-slate-200"
                  } ${dragId === p.id ? "opacity-50" : ""}`}
                  data-testid={`admin-gallery-photo-${p.id}`}
                >
                  <div className="relative">
                    <img src={p.url} alt={p.caption || "Gallery photo"} className="w-full h-44 object-cover pointer-events-none" loading="lazy" />
                    <span className="absolute top-2 left-2 bg-black/60 text-white rounded-lg p-1.5" title="Drag to reorder">
                      <GripVertical size={14} />
                    </span>
                  </div>
                  <div className="p-3 flex gap-2 items-center">
                    <input
                      data-testid={`admin-gallery-caption-edit-${p.id}`}
                      value={captionEdits[p.id] ?? p.caption ?? ""}
                      onChange={(e) => setCaptionEdits({ ...captionEdits, [p.id]: e.target.value })}
                      placeholder="No caption"
                      className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs flex-1 min-w-0"
                    />
                    {captionEdits[p.id] !== undefined && captionEdits[p.id] !== (p.caption ?? "") && (
                      <button
                        data-testid={`admin-gallery-caption-save-${p.id}`}
                        onClick={() => saveGalleryCaption(p.id)}
                        className="btn-ocean text-white text-xs font-bold px-3 py-1.5 rounded-lg shrink-0"
                      >
                        Save
                      </button>
                    )}
                    <button
                      data-testid={`admin-gallery-delete-${p.id}`}
                      onClick={() => deleteGalleryPhoto(p.id)}
                      className="text-slate-400 hover:text-red-500 transition-colors duration-150 p-1.5 shrink-0"
                      title="Remove photo"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
              {gallery.length === 0 && <p className="text-sm text-slate-400 col-span-full">No gallery photos yet — upload your first one above.</p>}
            </div>
          </div>
        )}

        {tab === "blocked" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Blocked dates</h2>
            <p className="text-sm text-slate-500 mb-5">Block dates from public booking (e.g. Royal Caribbean charter days or maintenance).</p>
            <div className="flex flex-wrap gap-3 mb-6">
              <select
                data-testid="admin-block-tour-select"
                value={newBlock.tour_id}
                onChange={(e) => setNewBlock({ ...newBlock, tour_id: e.target.value })}
                className="border border-slate-300 rounded-xl px-3 py-2.5 text-sm"
              >
                {tours.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <input
                data-testid="admin-block-date-input"
                type="date"
                value={newBlock.date}
                onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                className="border border-slate-300 rounded-xl px-4 py-2.5 text-sm"
              />
              <button data-testid="admin-block-date-button" onClick={addBlock} className="bg-slate-900 hover:bg-slate-700 text-white font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors duration-150">
                <Ban size={16} /> Block date
              </button>
            </div>
            <div className="space-y-2">
              {blocked.map((bd) => (
                <div key={bd.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-blocked-${bd.date}`}>
                  <p className="text-sm font-semibold text-slate-700">
                    {prettyDate(bd.date)} · <span className="text-slate-500 font-normal">{tours.find((t) => t.id === bd.tour_id)?.name || bd.tour_id}</span>
                  </p>
                  <button onClick={() => unblock(bd.id)} className="text-slate-400 hover:text-red-500 transition-colors duration-150" data-testid={`admin-unblock-${bd.date}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {blocked.length === 0 && <p className="text-sm text-slate-400">No blocked dates.</p>}
            </div>
          </div>
        )}
        {tab === "reviews" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Guest reviews</h2>
            <p className="text-sm text-slate-500 mb-5">New reviews are held for approval. Approve to publish on the home page, or delete.</p>
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className={`flex items-start justify-between gap-4 rounded-xl px-4 py-3 ${r.status === "pending" ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`} data-testid={`admin-review-${r.id}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-slate-800">{r.name}</p>
                      <span className="text-xs font-bold text-amber-500">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${r.status === "pending" ? "bg-amber-400 text-white" : "bg-emerald-100 text-emerald-700"}`}>
                        {r.status === "pending" ? "Pending approval" : "Live"}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {r.status === "pending" && (
                      <button onClick={() => approveReview(r.id)} className="text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-full transition-colors duration-150" data-testid={`admin-approve-review-${r.id}`}>
                        Approve
                      </button>
                    )}
                    <button onClick={() => deleteReview(r.id)} className="text-slate-400 hover:text-red-500 transition-colors duration-150" data-testid={`admin-delete-review-${r.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-sm text-slate-400">No reviews yet.</p>}
            </div>
          </div>
        )}
        {tab === "users" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Users &amp; passwords</h2>
            <p className="text-sm text-slate-500 mb-5">Add users, change passwords (min 8 characters) or remove accounts. Partners get access to the Partner Portal only.</p>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Add a new user</p>
              <div className="flex flex-wrap gap-3">
                <input
                  data-testid="admin-new-user-name-input"
                  placeholder="Name (e.g. Royal Caribbean)"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm w-48"
                />
                <input
                  data-testid="admin-new-user-email-input"
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm w-56"
                />
                <input
                  data-testid="admin-new-user-password-input"
                  type="password"
                  placeholder="Password (min 8)"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm w-44"
                />
                <select
                  data-testid="admin-new-user-role-select"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2 text-sm"
                >
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  data-testid="admin-create-user-button"
                  onClick={createUser}
                  className="btn-ocean text-white text-sm font-bold px-5 py-2 rounded-xl"
                >
                  Add User
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`admin-user-${u.email}`}>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{u.name || u.email}</p>
                    <p className="text-xs text-slate-500">{u.email} · <span className={`font-bold ${u.role === "admin" ? "text-sky-600" : "text-orange-500"}`}>{u.role}</span></p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      data-testid={`admin-user-password-input-${u.email}`}
                      type="password"
                      placeholder="New password"
                      value={pwInputs[u.id] || ""}
                      onChange={(e) => setPwInputs({ ...pwInputs, [u.id]: e.target.value })}
                      className="border border-slate-300 rounded-xl px-3 py-2 text-sm w-44"
                    />
                    <button
                      data-testid={`admin-user-password-save-${u.email}`}
                      onClick={() => changePassword(u.id)}
                      className="btn-ocean text-white text-sm font-bold px-4 py-2 rounded-xl"
                    >
                      Update
                    </button>
                    <button
                      data-testid={`admin-user-delete-${u.email}`}
                      onClick={() => deleteUser(u)}
                      className="text-slate-400 hover:text-red-500 transition-colors duration-150 p-2"
                      title="Delete user"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
